import { notifyManager, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import type { CampaignData } from '@/lib/types';
import { campaignDataAtom } from '@/store';

/**
 * Refetch one campaign, resolving once the fresh campaign is everywhere a caller reads it next (#6826).
 *
 * One request: invalidating the campaign's query refetches it once for all its observers and resolves when the new
 * data is cached. A second, chained refetch used to follow, rebuilding the whole campaign on the server again.
 * campaignDataAtom is written straight away rather than on the layout's next effect, and the closing yield lets the
 * query's observers render the fresh campaign before the caller carries on (a draft save or a create-ad navigation
 * reads it next).
 */
export const useRefetchCampaign = (campaignSlug: string) => {
  const queryClient = useQueryClient();
  const setCampaignData = useSetAtom(campaignDataAtom);

  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['campaign', campaignSlug] });
    const campaign = queryClient.getQueryData<CampaignData>(['campaign', campaignSlug]);
    if (campaign) {
      setCampaignData(campaign);
    }
    await new Promise<void>((resolve) => notifyManager.schedule(resolve));
  }, [queryClient, campaignSlug, setCampaignData]);
};

export const useRefetchCampaigns = () => {
  const queryClient = useQueryClient();
  const delayedRefetchTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Invalidating the ['campaigns', …] query triggers React Query to refetch; the
  // _authenticated sync effect (keyed on the query data) then repopulates the atoms
  // consumers still read. Awaiting the invalidation resolves once the refetch completes.
  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  }, [queryClient]);

  /**
   * Refetch immediately, then schedule follow-up refetches to pick up
   * async updates (e.g., AI-generated campaign names from Celery tasks).
   */
  const refetchWithDelayedUpdates = useCallback(async () => {
    // Clear any pending timers from a previous call
    delayedRefetchTimers.current.forEach(clearTimeout);
    delayedRefetchTimers.current = [];

    await refetch();

    // Schedule follow-up refetches to catch async name generation
    const delays = [3000, 6000, 12000];
    for (const delay of delays) {
      const timer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      }, delay);
      delayedRefetchTimers.current.push(timer);
    }
  }, [refetch, queryClient]);

  // Clean up pending timers on unmount
  useEffect(() => {
    return () => {
      delayedRefetchTimers.current.forEach(clearTimeout);
    };
  }, []);

  return { refetch, refetchWithDelayedUpdates };
};
